import { Carte, BoutonPrincipal, MessageErreur, Pastille } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { useInformerEntreprise, type LigneJournee } from '../../lib/useAbsences';
import { useToast } from '../ui/useToast';

/**
 * « Mail automatique à l'entreprise » (maquette) : le mail de la journée
 * choisie, tel que l'API l'a rédigé — celui qui est parti ou partirait —, ses
 * destinataires et le renvoi. Sans employeur à informer, la carte le dit.
 */
export function ApercuMailEntreprise({
  ligne,
  automatique,
}: {
  ligne: LigneJournee | null;
  automatique: boolean;
}): JSX.Element {
  const toast = useToast();
  const informer = useInformerEntreprise();
  // La dernière demi-journée qui a un mail : l'après-midi d'une journée
  // complète dit « journée complète ».
  const demi = ligne ? [...ligne.demiJournees].reverse().find((candidate) => candidate.apercu) : undefined;

  return (
    <Carte titre="Mail automatique à l’entreprise" sansBordure>
      <div className="space-y-3 p-4 sm:p-6">
        {!ligne && (
          <p className="text-sm text-codialis-gray">Choisissez une absence pour lire son mail.</p>
        )}
        {ligne && !demi && (
          <p className="text-sm text-codialis-gray">
            {ligne.provisoire
              ? 'La feuille n’est pas clôturée : le mail partira à la clôture.'
              : `${ligne.apprenant} n’a pas de contrat en vigueur : il n’y a pas d’employeur à informer.`}
          </p>
        )}
        {ligne && demi?.apercu && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Pastille texte={demi.informationLibelle} ton={demi.informationTon} />
              {ligne.entreprise && (
                <span className="min-w-0 break-all text-[11px] text-codialis-gray">
                  {ligne.entreprise.adresses.join(', ') || ligne.entreprise.denomination}
                </span>
              )}
            </div>
            <div className="space-y-1.5 rounded-xl bg-gray-50 p-4 text-xs text-codialis-navy">
              <div className="font-semibold">Objet : {demi.apercu.titre}</div>
              {demi.apercu.corps.split('\n\n').map((paragraphe) => (
                <p key={paragraphe} className="text-codialis-gray">
                  {paragraphe}
                </p>
              ))}
            </div>
            {demi.refusInformation && demi.information !== 'ENVOYEE' && (
              <p className="text-[11px] text-codialis-gray">{demi.refusInformation}</p>
            )}
            {informer.error instanceof ErreurApi && <MessageErreur message={informer.error.message} />}
            {demi.refusInformation === null && (
              <BoutonPrincipal
                disabled={informer.isPending}
                onClick={() =>
                  informer.mutate(demi.presenceId, { onSuccess: (reponse) => toast(reponse.message) })
                }
              >
                {demi.information === 'ENVOYEE' ? 'Renvoyer le mail' : 'Envoyer le mail'}
              </BoutonPrincipal>
            )}
          </>
        )}
        <p className="text-[11px] text-codialis-gray">
          {automatique
            ? 'Envoyé à la clôture de chaque demi-journée, à l’entreprise et au maître d’apprentissage, copie au pôle pédagogique. Le type d’absence (matin, après-midi, journée complète) est précisé automatiquement.'
            : 'L’envoi automatique est coupé dans le module d’alertes (« Absence apprenant ») : les mails partent d’ici, à la main.'}
        </p>
      </div>
    </Carte>
  );
}
